import { CheckIcon, CopyIcon, ChevronRightIcon, SquareArrowOutUpRightIcon, EllipsisIcon } from 'lucide-react';
import { useState } from 'react';


import { Checkbox } from '../ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Separator } from '../ui/separator';

import { cn } from '@/lib/utils';

export interface FileHeaderProps {
  /**
   * The file path to display in the header
   */
  filePath: string;
  expanded?: boolean;
  onToggleExpand?: () => void;
  viewed?: boolean;
  onToggleViewed?: () => void;
  /**
   * Optional className for the header
   */
  className?: string;
  owner?: string;
  repo?: string;
  sha?: string;
}

/**
 * FileHeader displays the file path and will later include collapse/expand and other controls.
 */
export function FileHeader({
  filePath,
  expanded = true,
  onToggleExpand,
  viewed = false,
  onToggleViewed,
  className,
  owner,
  repo,
  sha,
}: FileHeaderProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(filePath);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <div className={cn('flex items-center w-full justify-between gap-2 px-3.5 h-9', className)}>
      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-label={expanded ? 'Collapse file' : 'Expand file'}
          onClick={onToggleExpand}
          className="group cursor-pointer p-1"
        >
          <ChevronRightIcon
            className={cn('size-4 m-auto group-hover:text-blue-400 transition-transform duration-150', {
              'rotate-90': expanded,
            })}
          />
        </button>
        <div
          className="flex items-center gap-2 group cursor-pointer"
          aria-label="Copy file path"
          role="button"
          onClick={handleCopy}
        >
          <span className="relative text-sm text-foreground flex-1 truncate">{filePath}</span>
          <div className="flex items-center gap-2 w-10">
            {copied ? (
              <CheckIcon className="size-4 text-green-600 transition-opacity duration-150" />
            ) : (
              <div
                onClick={handleCopy}
                className={cn(
                  'transition-opacity duration-150',
                  copied ? 'opacity-100' : 'opacity-0',
                  'group-hover:opacity-100'
                )}
              >
                <CopyIcon className="size-4 text-muted-foreground hover:text-primary transition-colors" />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5 cursor-pointer select-none" onClick={onToggleViewed} role="button">
          <Checkbox checked={viewed} />
          <span className="text-xs text-muted-foreground">Viewed</span>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger className="hover:bg-stone-200 flex items-center justify-center rounded p-1.5 cursor-pointer">
            <EllipsisIcon className="size-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <a
              href={`https://github.com/${owner}/${repo}/blob/${sha}/${filePath}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <DropdownMenuItem className="flex items-center gap-2 cursor-pointer">
                <SquareArrowOutUpRightIcon className="size-4" strokeWidth={1} />
                View file on GitHub
              </DropdownMenuItem>
            </a>
            <a
              href={`https://github.com/${owner}/${repo}/raw/${sha}/${filePath}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <DropdownMenuItem className="flex items-center gap-2 cursor-pointer">
                <SquareArrowOutUpRightIcon className="size-4" strokeWidth={1} />
                View raw
              </DropdownMenuItem>
            </a>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
